/**
 * ================================================================
 * NOTIFICATION LISTENER HOOK - React Integration Layer
 * ================================================================
 *
 * Custom hook to handle push notification events
 * ✅ Can call other hooks (useQueryClient, useNavigate)
 * ✅ Runs in React context (App.tsx)
 * ✅ Event-driven badge updates
 * ✅ Smooth navigation without page reload
 *
 * @author MeBi Team
 * @version 3.0.0
 */

import { Capacitor } from "@capacitor/core";
import { useEffect } from "react";
import { PushNotifications } from "@capacitor/push-notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { UNREAD_COUNT_KEY } from "./useNotificationBadge";
import apiClient from "@/lib/apiClient";
import { notificationApi } from "@/features/notifications/services/notification.api";

let listenersInitialized = false;

const readDataString = (
  data: Record<string, unknown> | undefined,
  keys: string[],
): string | null => {
  if (!data) {
    return null;
  }

  for (const key of keys) {
    const value = data[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }

  return null;
};

export const resolveNotificationRedirectPath = (
  data: Record<string, unknown> | undefined,
): string | null => {
  const routePath = readDataString(data, ["route_url", "route", "path"]);
  if (routePath) {
    return routePath.startsWith("/") ? routePath : `/${routePath}`;
  }

  const competitionId = readDataString(data, [
    "competitionId",
    "competition_id",
    "tournamentId",
    "tournament_id",
  ]);
  if (competitionId) {
    return `/competition/${competitionId}`;
  }

  const matchId = readDataString(data, ["matchId", "match_id"]);
  if (matchId) {
    return "/my-schedule";
  }

  const notificationType = (readDataString(data, ["type"]) ?? "").toLowerCase();
  if (
    notificationType === "organizer_approved" ||
    notificationType === "organizer_rejected"
  ) {
    return "/tabs/home";
  }

  return "/notifications";
};

/**
 * Hook to handle push notification events
 */
export const useNotificationListener = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (listenersInitialized) {
      return;
    }

    type NotificationToken = { value: string };
    type NotificationAction = {
      notification?: {
        data?: {
          notificationId?: string;
          type?: string;
        } & Record<string, unknown>;
      };
    };

    const setupListeners = async () => {
      // Only run on native platforms
      if (!Capacitor.isNativePlatform()) {
        return;
      }

      listenersInitialized = true;

      // ===== LISTENER 1: Registration Success =====
      await PushNotifications.addListener(
        "registration",
        async (token: NotificationToken) => {
          try {
            await notificationApi.sendTokenToBackend(token.value);
          } catch (error) {
            console.error("❌ Failed to sync FCM token:", error);
          }
        },
      );

      // ===== LISTENER 2: Registration Error =====
      await PushNotifications.addListener(
        "registrationError",
        (error: unknown) => {
          console.error("❌ Registration error:", error);
        },
      );

      // ===== LISTENER 3: Foreground Notification =====
      await PushNotifications.addListener("pushNotificationReceived", () => {
        // ✅ EVENT-DRIVEN: Update badge instantly
        queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
        // Also invalidate organization status just in case (e.g. approved while in app)
        queryClient.invalidateQueries({ queryKey: ["organization-status"] });

        // Optional: Show in-app toast
        // toast.success(notification.title, { description: notification.body });
      });

      // ===== LISTENER 4: Notification Tap (Background/Killed) =====
      await PushNotifications.addListener(
        "pushNotificationActionPerformed",
        async (action: NotificationAction) => {
          const data = action.notification?.data;
          const notificationType = readDataString(data, ["type"]);

          if (
            notificationType === "organizer_approved" ||
            notificationType === "organizer_rejected"
          ) {
            // Invalidate and refetch profile
            await queryClient.invalidateQueries({ queryKey: ["profile"] });
            await queryClient.invalidateQueries({
              queryKey: ["organization-status"],
            });
            await queryClient.refetchQueries({ queryKey: ["profile"] });
          }

          // Mark notification as read
          if (typeof data?.notificationId === "string") {
            try {
              await apiClient.patch(
                `/notifications/${data.notificationId}/read`,
              );
              queryClient.invalidateQueries({ queryKey: UNREAD_COUNT_KEY });
            } catch (err) {
              console.error("Failed to mark as read:", err);
            }
          }

          const redirectPath = resolveNotificationRedirectPath(data);
          if (redirectPath) {
            navigate(redirectPath, { replace: true });
          }
        },
      );
    };

    setupListeners();
  }, [queryClient, navigate]);
};
